// src/components/ClientList/ClientCard.js
import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiBaseUrl } from '../../util/api';
import { getOwnListingsById } from '../../containers/ManageListingsPage/ManageListingsPage.duck';
import css from './ClientCard.module.css';

const AVATAR_COLORS = ['#6366f1', '#2ebff7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const avatarColor = name => {
  const code = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const formatPhone = phone => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1')
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return phone;
};

const STATUS_DOT = { active: '#10b981', prospect: '#f59e0b', inactive: '#9ca3af' };

const ClientCard = ({ client, onUpdate, currentUser, onOpenListingModal, onSendDm, selected, onToggleSelect, density }) => {
  const history = useHistory();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const overflowRef = useRef(null);

  const { currentPageResultIds } = useSelector(state => state.ManageListingsPage);
  const allListings = useSelector(state => getOwnListingsById(state, currentPageResultIds));
  const publishedListings = allListings.filter(l => l.attributes?.state === 'published');

  useEffect(() => {
    fetch(`${apiBaseUrl()}/api/exercise/trainer/subscription-status`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (data.success) setIsSubscribed(data.isSubscribed); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = e => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setShowOverflow(false);
      }
    };
    if (showOverflow) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOverflow]);

  const getStatusBadgeClass = status => {
    switch (status) {
      case 'active': return css.statusActive;
      case 'prospect': return css.statusProspect;
      case 'inactive': return css.statusInactive;
      default: return css.statusDefault;
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const handleStatusChange = async newStatus => {
    setShowOverflow(false);
    try {
      const res = await fetch(`${apiBaseUrl()}/api/clients/${client.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) onUpdate();
    } catch (e) {
      console.error('Failed to update status', e);
    }
  };

  const fullName = `${client.first_name} ${client.last_name}`;
  const initials = `${(client.first_name || '').charAt(0)}${(client.last_name || '').charAt(0)}`.toUpperCase();
  const dotColor = STATUS_DOT[client.status] || '#9ca3af';
  const phone = formatPhone(client.phone);

  // CR-5: Primary action depends on status
  const handlePrimaryAction = () => {
    if (client.status === 'prospect') {
      if (publishedListings.length === 0) {
        alert('You have no published listings. Please create a listing first.');
        return;
      }
      onOpenListingModal(client);
    } else {
      history.push(`/clients/${client.id}`);
    }
  };

  const primaryLabel = client.status === 'prospect' ? 'Send Listing' : 'View';

  const tdPad = density === 'compact' ? css.tdCompact : css.tdComfortable;

  return (
    <tr className={`${css.tableRow} ${selected ? css.rowSelected : ''}`}>
      {/* Checkbox */}
      <td className={`${css.checkCell} ${tdPad}`}>
        <input
          type="checkbox"
          className={css.rowCheckbox}
          checked={!!selected}
          onChange={() => onToggleSelect(client.id)}
          aria-label={`Select ${fullName}`}
        />
      </td>

      {/* Client Info */}
      <td className={`${css.clientCell} ${tdPad}`}>
        <div className={css.clientInfo}>
          <div
            className={css.avatar}
            style={{ background: avatarColor(fullName) }}
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className={css.clientDetails}>
            <div className={css.clientName}>{fullName}</div>
            <div className={css.clientEmail}>{client.email}</div>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className={tdPad}>
        <span className={`${css.statusBadge} ${getStatusBadgeClass(client.status)}`}>
          <span className={css.statusDot} style={{ background: dotColor }} aria-hidden="true" />
          {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
        </span>
      </td>

      {/* Programs */}
      <td className={`${css.centerText} ${tdPad}`}>
        <span className={css.metricValue}>{client.active_programs || 0}</span>
      </td>

      {/* Last Workout */}
      <td className={tdPad}>
        <span className={css.dateText}>{formatDate(client.last_workout_date)}</span>
      </td>

      {/* Phone */}
      <td className={tdPad}>
        {phone ? (
          <span className={css.phoneText}>{phone}</span>
        ) : (
          <span className={css.noContact}>—</span>
        )}
      </td>

      {/* Actions (CR-5) */}
      <td className={`${css.actionsCell} ${tdPad}`}>
        <button className={css.btnPrimary} onClick={handlePrimaryAction}>
          {primaryLabel}
        </button>

        <div className={css.overflow} ref={overflowRef}>
          <button
            className={css.btnOverflow}
            onClick={() => setShowOverflow(v => !v)}
            aria-label="More actions"
            aria-expanded={showOverflow}
          >
            ···
          </button>
          {showOverflow && (
            <div className={css.overflowMenu} role="menu">
              <button
                className={css.overflowItem}
                role="menuitem"
                onClick={() => { setShowOverflow(false); history.push(`/clients/${client.id}`); }}
              >
                View details
              </button>

              {client.client_id && onSendDm && (
                <button
                  className={css.overflowItem}
                  role="menuitem"
                  onClick={() => { setShowOverflow(false); onSendDm(client); }}
                >
                  Send DM
                </button>
              )}

              {client.status !== 'prospect' && (
                <button
                  className={css.overflowItem}
                  role="menuitem"
                  onClick={() => {
                    setShowOverflow(false);
                    if (publishedListings.length === 0) {
                      alert('You have no published listings. Please create a listing first.');
                      return;
                    }
                    onOpenListingModal(client);
                  }}
                >
                  Send listing
                </button>
              )}

              {isSubscribed && (
                <button
                  className={css.overflowItem}
                  role="menuitem"
                  onClick={() => { setShowOverflow(false); history.push(`/clients/${client.id}?tab=programs&action=assign`); }}
                >
                  Assign program
                </button>
              )}

              {client.status !== 'active' && (
                <button className={css.overflowItem} role="menuitem" onClick={() => handleStatusChange('active')}>
                  Mark as active
                </button>
              )}
              {client.status !== 'prospect' && (
                <button className={css.overflowItem} role="menuitem" onClick={() => handleStatusChange('prospect')}>
                  Mark as prospect
                </button>
              )}
              {client.status !== 'inactive' && (
                <button className={css.overflowItem} role="menuitem" onClick={() => handleStatusChange('inactive')}>
                  Mark as inactive
                </button>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ClientCard;
